"use client";

import { useCallback, useState } from "react";
import { ElevenLabsClient } from "@elevenlabs/elevenlabs-js";
import { unfinishedRoomPrompt } from "@rebuild/shared";
import { useDispatch, useSelector } from "@/redux/store";
import { addConversationData } from "@/redux/slices/estimation.slice";
import type { AppState } from "@/redux/rootReducer";
import { Mic, MicOff } from "lucide-react";
import type { Estimation } from "@/interfaces/estimation.interface";
import { appSelector } from "@/redux/selectors/app.selector";
import EstimateProvider from "@/providers/estimate.provider";
import type { NoteType } from "@rebuild/shared";
import { getConversationInputParams, EstimateType } from "@rebuild/shared";
import { getConversationType } from "./getConversationType";
import { transformTranscriptToString } from "./ConversationUtils";
import type { HookOptions, Status } from "@elevenlabs/react";
import type { EstimateUploadState } from "../UploadEstimationFiles";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import systemPromptProvider from "@/providers/systemPrompt.provider";

const VITE_ELEVEN_LABS_CONTENTS_AGENT_ID = import.meta.env.VITE_ELEVEN_LABS_CONTENTS_AGENT_ID;
const VITE_ELEVEN_LABS_MIT_AGENT_ID = import.meta.env.VITE_ELEVEN_LABS_AGENT_ID;
const VITE_ELEVEN_LABS_RECON_AGENT_ID = import.meta.env.VITE_ELEVEN_LABS_RECON_AGENT_ID;

const ESTIMATE_TYPE_TO_AGENT_ID: Record<EstimateType, string | undefined> = {
  [EstimateType.CONTENTS]: VITE_ELEVEN_LABS_CONTENTS_AGENT_ID,
  [EstimateType.MITIGATION]: VITE_ELEVEN_LABS_MIT_AGENT_ID,
  [EstimateType.FIRE_MITIGATION]: undefined,
  [EstimateType.RECONSTRUCTION]: undefined,
  [EstimateType.MOLD]: VITE_ELEVEN_LABS_MIT_AGENT_ID,
  [EstimateType.BIOHAZARD]: undefined,
  [EstimateType.TIME_AND_MATERIAL]: undefined,
};

const getAgentIdForEstimateType = (estimateType: EstimateType, reconVoiceAgentEnabled: boolean): string | undefined => {
  if (estimateType === EstimateType.RECONSTRUCTION) {
    return reconVoiceAgentEnabled ? VITE_ELEVEN_LABS_RECON_AGENT_ID : undefined;
  }
  return ESTIMATE_TYPE_TO_AGENT_ID[estimateType];
};

type ElevenLabsConversationHook = {
  startSession: (options: HookOptions) => Promise<string>;
  endSession: () => Promise<void>;
  status: Status;
};

export function Conversation({
  estimateId,
  conversation,
  onScopeNotesReceived,
  onProjectNameReceived,
  onLoadingStateChange,
  onUpdate,
  estimation,
  noteType,
  estimateType,
}: {
  estimateId: string;
  conversation: ElevenLabsConversationHook;
  onScopeNotesReceived?: (scopeNotes: string) => void;
  onProjectNameReceived?: (projectName: string) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
  onUpdate?: (updates: Partial<EstimateUploadState>) => void;
  estimation?: Estimation;
  noteType: NoteType;
  estimateType: EstimateType;
}) {
  const user = useSelector(appSelector.user);
  const { isFeatureEnabled } = useFeatureFlags();
  const reconVoiceAgentEnabled = isFeatureEnabled("RECON_VOICE_AGENT_ENABLED");

  const dispatch = useDispatch();
  const conversationContext = useSelector(
    (state: AppState) =>
      state.estimation.conversationContext?.[estimateId || ""] || {
        stopCount: 0,
        accumulatedScopeNotes: [],
        accumulatedAffectedRooms: [],
        conversationHistory: [],
      },
  );

  const [conversationId, setConversationId] = useState<string | null>(null);

  const elevenLabsClient = new ElevenLabsClient({
    apiKey: import.meta.env.VITE_ELEVEN_LABS_API_KEY,
  });

  // Poll the converation data until the status is "done".  We do this after
  // a conversation is stopped, so there should not be too many requests sent.
  const getConversationData = async (conversationId: string) => {
    while (true) {
      const convo = await elevenLabsClient.conversationalAi.conversations.get(conversationId);
      if (convo.status === "done") {
        return convo;
      }

      await new Promise((r) => setTimeout(r, 500));
    }
  };

  const getFormattedContext = useCallback(() => {
    // if no stops, it's the first conversation and we don't need to add any context.
    if (conversationContext.stopCount === 0) {
      return "";
    }

    let formatted = `\n\n## Previous Conversation Context\n`;
    formatted += `Total conversation stops: ${conversationContext.stopCount}\n\n`;

    if (conversationContext.accumulatedScopeNotes.length > 0) {
      formatted += `### Previously collected scope notes:\n`;
      conversationContext.accumulatedScopeNotes.forEach((notes, index) => {
        formatted += `${index + 1}. ${notes}\n`;
      });
      formatted += `\nContinue gathering additional information, building on what has already been collected.\n`;
    }

    if (conversationContext.accumulatedAffectedRooms.length > 0) {
      formatted += `### Previously identified affected rooms:\n`;
      conversationContext.accumulatedAffectedRooms.forEach((rooms, index) => {
        formatted += `${index + 1}. ${rooms}\n`;
      });
      formatted += `\nBuild on the previously identified rooms when collecting new information.\n`;
    }

    if (conversationContext.unfinishedRoomTranscript) {
      formatted += `\n\n## In-Progress Room: \n`;
      formatted += unfinishedRoomPrompt(conversationContext.unfinishedRoomTranscript);
    }
    return formatted;
  }, [conversationContext]);

  const startConversation = useCallback(async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("getUserMedia not supported. Please use HTTPS or a supported browser.");
      }

      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });
      if (estimateType === undefined) {
        throw new Error(`Unable to start conversation since estimate type is undefined`);
      }

      const conversationType = getConversationType({
        stopCount: conversationContext.stopCount,
        creationSource: estimation?.creationSource,
        scopeNoteCount: (estimation?.scopeNoteObjects ?? []).length,
      });
      const customSystemPromptResult = await systemPromptProvider.getSystemPrompt(noteType);
      const customSystemPrompt = customSystemPromptResult.success ? customSystemPromptResult.data : null;

      let estimateInfo = undefined;
      if (estimation) {
        estimateInfo = {
          projectName: estimation.projectName,
          propertyAddress: estimation.propertyAddress,
          mitigationDetails: estimation.mitigationDetails,
          scopeNoteObjects:
            estimation.scopeNoteObjects?.map((scopeNote) => ({
              ...scopeNote,
              text: scopeNote.text ?? "",
            })) ?? [],
        };
      }

      const { firstMessage, systemPrompt } = getConversationInputParams(
        estimateType,
        noteType,
        customSystemPrompt,
        conversationType,
        {
          unfinishedRoomName: conversationContext.unfinishedRoomName,
          unansweredQuestion: conversationContext.unansweredQuestion,
        },
        getFormattedContext(),
        estimateInfo,
        user,
      );

      const agentId = getAgentIdForEstimateType(estimateType, reconVoiceAgentEnabled);

      if (agentId === undefined) {
        throw new Error(`Can't find an eleven labs agent for this estimate type, can't start conversation`);
      }

      // Start convo
      const conversationId = await conversation.startSession({
        agentId,
        userId: user?.lassoUserId,
        overrides: {
          // this will update the system prompt for this individual part of the conversation
          // based on the global context (to enable stopping/starting per room)
          agent: {
            prompt: { prompt: systemPrompt },
            firstMessage,
          },
        },
      });
      setConversationId(conversationId);
    } catch (error) {
      console.error("Failed to start conversation:", error);
      // Show user-friendly error message
      alert("Microphone access failed. Please ensure you're using HTTPS or try a different browser.");
    }
  }, [conversation, getFormattedContext, conversationContext, noteType]);

  const stopConversation = useCallback(async () => {
    await conversation.endSession();
    // retrieve data collected from Conversation id
    if (!conversationId || !estimateId) {
      return;
    }

    onLoadingStateChange?.(true);
    const conversationData = await getConversationData(conversationId);
    const transcript = conversationData.transcript;
    const conversationAnalysis = conversationData.analysis?.dataCollectionResults || {};

    // Grab mitigation details based on conversation analysis
    let projectClass: number | undefined;
    let projectCat: number | undefined;
    if (conversationAnalysis?.project_class?.value) {
      projectClass = conversationAnalysis.project_class.value as number;
      onUpdate?.({
        mitigationDetails: {
          ...estimation?.mitigationDetails,
          class: projectClass,
        },
      });
    }
    if (conversationAnalysis && conversationAnalysis.project_cat) {
      projectCat = conversationAnalysis.project_cat.value as number | undefined;
      if (projectCat && projectCat === 3) {
        onUpdate?.({
          mitigationDetails: {
            ...estimation?.mitigationDetails,
            cat3Contamination: true,
          },
        });
      }
    }

    // "additional notes" are notes unrelated to the estimate
    let additionalNotes: string | undefined;
    if (conversationAnalysis && conversationAnalysis.additional_notes) {
      additionalNotes = conversationAnalysis.additional_notes.value as string | undefined;
    }

    // Update scope notes based on the most recent conversation
    let scopeNotes: string | undefined;
    if (conversationAnalysis && conversationAnalysis.scope_notes) {
      const convoScopeNotes = conversationAnalysis.scope_notes.value as string | undefined;
      scopeNotes = convoScopeNotes ?? "";
      if (estimateType === EstimateType.MITIGATION) {
        const additionalNotesString = additionalNotes ? `\n\nAdditional Notes:\n${additionalNotes}` : "";
        let catClassNotes = "";
        if (projectCat) {
          catClassNotes += `Cat: ${projectCat}\n`;
        }
        if (projectClass) {
          catClassNotes += `Class: ${projectClass}\n`;
        }
        scopeNotes = `${catClassNotes}\n\n${scopeNotes}\n\n${additionalNotesString}`;
      }

      // If we're dealing with many stops, we need to combine transcripts to get the scope note summary.
      if (conversationContext.stopCount > 0) {
        // combine transcripts to get the scope note summary
        const newTranscript = transformTranscriptToString(transcript);
        const combinedTranscripts = [...(conversationContext.transcripts || []), newTranscript].join("\n");
        try {
          const enhancedScopeNotes = await EstimateProvider.transcriptToScopeNotes(
            combinedTranscripts,
            estimateType ?? EstimateType.MITIGATION,
          );
          if (enhancedScopeNotes) {
            scopeNotes = enhancedScopeNotes.scopeNotes;
          }
        } catch (error) {
          console.error("Error calling transcriptToScopeNotes:", error);
        }
      }
      onScopeNotesReceived?.(scopeNotes);
    }

    // Extract affected rooms from conversation analysis
    let affectedRooms: string | undefined;
    if (conversationAnalysis && conversationAnalysis.affected_rooms) {
      affectedRooms = conversationAnalysis.affected_rooms.value as string;
    }

    // Set project name based on conversation if it does not already exist
    if (conversationAnalysis && conversationAnalysis.project_name) {
      const projectName = conversationAnalysis.project_name.value as string | undefined;
      if (projectName) {
        onProjectNameReceived?.(projectName);
      }
    }

    let projectAddress: string | undefined;
    if (conversationAnalysis && conversationAnalysis.project_address) {
      projectAddress = conversationAnalysis.project_address.value as string | undefined;
    }

    let unfinishedRoomTranscript: string | undefined;
    if (conversationAnalysis && conversationAnalysis.unfinished_room_transcript) {
      unfinishedRoomTranscript = conversationAnalysis.unfinished_room_transcript.value as string | undefined;
    }
    let unfinishedRoomName: string | undefined;
    if (conversationAnalysis && conversationAnalysis.unfinished_room_name) {
      unfinishedRoomName = conversationAnalysis.unfinished_room_name.value as string | undefined;
    }
    let unansweredQuestion: string | undefined;
    if (conversationAnalysis && conversationAnalysis.unanswered_question) {
      unansweredQuestion = conversationAnalysis.unanswered_question.value as string | undefined;
    }

    // Update Redux with conversation data
    dispatch(
      addConversationData({
        estimateId,
        conversationId,
        scopeNotes,
        affectedRooms,
        projectAddress,
        unfinishedRoomTranscript,
        unfinishedRoomName,
        unansweredQuestion,
        transcript: transformTranscriptToString(transcript),
      }),
    );

    // Update conversationIds directly in newEstimation object
    if (onUpdate) {
      onUpdate({
        conversationIds: [...(estimation?.conversationIds || []), conversationId],
      });
    }

    setConversationId(null);
    onLoadingStateChange?.(false);
  }, [conversation, conversationId, onScopeNotesReceived, onProjectNameReceived, estimateId, dispatch, onLoadingStateChange]);

  return getAgentIdForEstimateType(estimation?.estimateType as EstimateType, reconVoiceAgentEnabled) === undefined ? (
    <div className="flex flex-col gap-2">
      <div className="text-center text-sm">Voice assistant not yet available for reconstruction estimates</div>
    </div>
  ) : (
    <div className="flex flex-col items-center gap-2">
      <h3 className="text-lg font-medium text-gray-800">Voice Assistant</h3>
      <button
        onClick={conversation.status === "connected" ? stopConversation : startConversation}
        className={`flex items-center gap-2 rounded bg-gray-200 px-4 py-2 text-black`}
        disabled={conversation.status === "connecting"}
      >
        {conversation.status === "connected" ? (
          <>
            <MicOff className="h-4 w-4" />
            Pause Conversation
          </>
        ) : conversation.status === "connecting" ? (
          <>
            <Mic className="h-4 w-4" />
            Connecting...
          </>
        ) : (
          <>
            <Mic className="h-4 w-4" />
            {conversationContext.stopCount > 0 ? "Resume Conversation" : "Start Conversation"}
          </>
        )}
      </button>
    </div>
  );
}
